// AdminDashboard.tsx (UI only, without AppShell wrapper) + uses external PostCard with carousel
// Notes:
// - Updated to use <PostCard /> that includes a built-in carousel and modal.
// - Pass the full images array and coverImageId so preview can scroll.

import React, { useEffect, useMemo, useState } from 'react';
import {
    Group,
    Button,
    Table,
    ActionIcon,
    Text,
    TextInput,
    Textarea,
    Modal,
    Card,
    Stack,
    Badge,
    Avatar,
    ScrollArea,
    Divider,
    Tooltip,
    rem,
    Notification,
    SimpleGrid,
} from '@mantine/core';
import { useDisclosure, useElementSize } from '@mantine/hooks';
import { useForm } from '@mantine/form';
import { Dropzone, IMAGE_MIME_TYPE, FileWithPath } from '@mantine/dropzone';
import {
    IconPlus,
    IconSearch,
    IconEdit,
    IconTrash,
    IconUpload,
    IconPhoto,
    IconX,
    IconCheck,
    IconAlertCircle,
    IconStar,
} from '@tabler/icons-react';
import './AdminDashboard.module.css';
import PostCard from '../../components/card/PostCard.tsx';
import { serverAccessor } from '../../accessors/serverAccessor.ts';
import axios from 'axios';
import { useAuth } from '../../hooks/useAuth.tsx';
import type PostInterface from '../../interfaces/PostInterface.ts';
import type PostImageInterface from '../../interfaces/PostImageInterface.ts';
import Seo from '../../components/Seo.tsx';
import { useTranslation } from 'react-i18next';

// --- helpers: data URL -> Blob, content type, file extension ---
function dataUrlToBlob(dataUrl: string): { blob: Blob; contentType: string; ext: string; } {
    const [header, b64] = dataUrl.split(',');
    const m = header.match(/^data:(.*?);base64$/i);
    const contentType = m?.[1] || 'application/octet-stream';
    const ext = contentType.split('/')[1] || 'bin';
    const bin = atob(b64);
    const len = bin.length;
    const arr = new Uint8Array(len);
    for (let i = 0; i < len; i++) arr[i] = bin.charCodeAt(i);
    return { blob: new Blob([arr], { type: contentType }), contentType, ext };
}

// --- upload a blob to S3 using the presigned url ---
async function uploadBlobToS3(uploadUrl: string, blob: Blob, contentType: string): Promise<void> {
    const res = await axios.put(uploadUrl, blob, {
        headers: { 'Content-Type': contentType }
    });
    if (res.status !== 200) throw new Error('S3 upload failed');
}

// ===== Utils =====
const genId = () => (typeof crypto !== 'undefined' && 'randomUUID' in crypto ? (crypto as any).randomUUID() : `id_${Math.random().toString(36).slice(2, 10)}`);
const fileToPreview = (file: FileWithPath): Promise<string> => new Promise((resolve) => { const reader = new FileReader(); reader.onload = () => resolve(String(reader.result)); reader.readAsDataURL(file); });

// ===== Image Uploader =====
function ImageUploader({
    postId,
    images,
    onAdd,
    onRemove,
    coverImageId,
    onSetCover,
}: {
    postId: string;
    images: PostImageInterface[];
    onAdd: (imgs: PostImageInterface[]) => void;
    onRemove: (imageId: string) => void;
    coverImageId: string;
    onSetCover: (imageId: string) => void;
}) {
    return (
        <Stack gap="sm">
            <Dropzone
                accept={{ 'image/*': [] }}
                multiple
                maxFiles={20}
                onDrop={async (files) => {
                    const previews = await Promise.all(files.map(fileToPreview));

                    // find next order start (handles existing images and missing order)
                    const existingOrders = images
                        .map((i) => (Number.isFinite(i.order) ? i.order : null))
                        .filter((v): v is number => v !== null);
                    const nextOrderStart = (existingOrders.length ? Math.max(...existingOrders) : -1) + 1;

                    const newImgs: PostImageInterface[] = previews.map((url, index) => ({
                        imageId: genId(),
                        postId,                          // ✅ include postId
                        order: nextOrderStart + index,   // ✅ append after current max
                        url,
                    }));

                    onAdd(newImgs);
                }}
                onReject={(items) => console.warn('Rejected files:', items)}
                className="ad-dropzone"
            >
                <div style={{ textAlign: 'center' }}>
                    <div>Drag images here, or click to select</div>
                    <div style={{ fontSize: 12, opacity: 0.7 }}>JPG • PNG • WEBP, multiple allowed</div>
                </div>
            </Dropzone>

            {images.length === 0 ? (
                <Card withBorder padding="lg" radius="md" className="ad-empty">
                    <Stack align="center" gap={8}>
                        <IconPhoto />
                        <Text size="sm" c="dimmed">No images yet</Text>
                    </Stack>
                </Card>
            ) : (
                <ScrollArea h={180} offsetScrollbars>
                    <Group wrap="nowrap" gap="md">
                        {images.map((img) => (
                            <Card key={img.imageId} className={`ad-thumb ${coverImageId === img.imageId ? 'ad-thumb-cover' : ''}`} withBorder>
                                <div className="ad-thumb-img" style={{ backgroundImage: `url(${img.url})` }} />
                                <Group justify="apart" mt="xs">
                                    <Group gap={6}>{coverImageId === img.imageId && (<Badge leftSection={<IconStar size={12} />} color="yellow" variant="light">Cover</Badge>)}</Group>
                                    <Group gap={6}>
                                        <Tooltip label={coverImageId === img.imageId ? 'This is the cover' : 'Set as cover'}>
                                            <ActionIcon variant="light" onClick={() => onSetCover(img.imageId)} aria-label="set-cover"><IconStar size={16} /></ActionIcon>
                                        </Tooltip>
                                        <Tooltip label="Remove image">
                                            <ActionIcon color="red" variant="light" onClick={() => onRemove(img.imageId)} aria-label="remove-image"><IconX size={16} /></ActionIcon>
                                        </Tooltip>
                                    </Group>
                                </Group>
                            </Card>
                        ))}
                    </Group>
                </ScrollArea>
            )}
        </Stack>
    );
}

// ===== Post Form Modal (create & edit) with Preview =====
function PostFormModal({ saving, opened, onClose, initial, onSubmit }: { saving: boolean; opened: boolean; onClose: () => void; initial?: PostInterface | null; onSubmit: (post: PostInterface) => void; }) {
    const isEditing = Boolean(initial);
    const defaultValues = React.useMemo<PostInterface>(() => {
        if (initial) return initial; // editing: use provided values
        return {
            postId: genId(),          // fresh id every open for "New"
            title: '',
            description: '',
            coverImageId: '',
            images: [],
        };
        // include `opened` so new-id regenerates per open in "New" mode
    }, [initial, opened]);
    const form = useForm<PostInterface>({ initialValues: initial ?? { postId: genId(), title: '', description: '', coverImageId: '', images: [] }, validate: { title: (v) => (v.trim().length < 3 ? 'Title must be at least 3 characters' : null), description: (v) => (v.trim().length < 10 ? 'Description must be at least 10 characters' : null), coverImageId: (_v, values) => (values.images.length > 0 && !values.coverImageId ? 'Pick a cover image' : null), }, });
    // Each time the modal opens in "New" mode, generate a brand-new default

    const handleSave = () => { const valid = form.validate(); if (valid.hasErrors) return; onSubmit(form.values); onClose(); };

    // When initial/default changes (new vs edit or changed item), reset the form
    React.useEffect(() => {
        form.setValues(defaultValues);
        form.resetDirty();
    }, [defaultValues]); // runs on open/new/edit switches


    return (
        <Modal
            opened={opened}
            onClose={onClose}
            title={initial ? 'Edit post' : 'New post'}
            size="xl"
            radius="md"
            keepMounted={false} // optional: unmount content when closed
        >
            <Stack>
                <TextInput label="Title" placeholder="e.g. Bright 2BR in Chamberí" {...form.getInputProps('title')} required />
                <Textarea label="Description" minRows={4} autosize {...form.getInputProps('description')} required />

                <Divider label="Images" labelPosition="center" my="sm" />
                <ImageUploader
                    postId={form.values.postId}
                    images={form.values.images}
                    onAdd={(newImgs) => { const merged = [...form.values.images, ...newImgs]; form.setFieldValue('images', merged); if (!form.values.coverImageId && merged.length > 0) { form.setFieldValue('coverImageId', merged[0].imageId); } }}
                    onRemove={(id) => {
                        // make sure order numbers are contiguous
                        const normalizeOrder = (arr: PostImageInterface[]) =>
                            arr
                                .sort((a, b) => (a.order ?? Infinity) - (b.order ?? Infinity))
                                .map((img, idx) => ({ ...img, order: idx }));
                        const remaining = normalizeOrder(form.values.images.filter(i => i.imageId !== id));
                        form.setFieldValue('images', remaining);
                        if (form.values.coverImageId === id) { form.setFieldValue('coverImageId', remaining[0]?.imageId || ''); }

                    }}
                    coverImageId={form.values.coverImageId}
                    onSetCover={(id) => form.setFieldValue('coverImageId', id)}
                />

                <Group justify="apart" mt="xs">
                    <Text size="sm" c="dimmed">{form.values.images.length} image{form.values.images.length === 1 ? '' : 's'} • Cover set: {Boolean(form.values.coverImageId) ? 'Yes' : 'No'}</Text>
                    <Group>
                        <Button variant="light" onClick={onClose} leftSection={<IconX size={16} />}>Cancel</Button>
                        <Button onClick={handleSave} loading={saving} leftSection={<IconCheck size={16} />}>{initial ? 'Save changes' : 'Create post'}</Button>
                    </Group>
                </Group>

                {/* Live user-facing preview (carousel + modal inside) */}
                <Divider label="Preview" labelPosition="center" my="sm" />
                <PostCard
                    post={form.values}
                    width="100%"
                    height={420}
                />
            </Stack>
        </Modal>
    );
}

// ===== Posts Table =====
function PostsTable({ posts, onEdit, onDelete, filter }: { posts: PostInterface[]; onEdit: (post: PostInterface) => void; onDelete: (postId: string) => void; filter: string; }) {
    const filtered = useMemo(() => { const f = filter.trim().toLowerCase(); if (!f) return posts; return posts.filter((p) => [p.title, p.description].some((s) => s.toLowerCase().includes(f))); }, [posts, filter]);
    return (
        <Card withBorder radius="md" className="ad-card">
            <ScrollArea>
                <Table highlightOnHover withTableBorder>
                    <Table.Thead>
                        <Table.Tr>
                            <Table.Th style={{ width: rem(56) }}>Cover</Table.Th>
                            <Table.Th>Title</Table.Th>
                            <Table.Th>Description</Table.Th>
                            <Table.Th style={{ width: rem(140) }}>Images</Table.Th>
                            <Table.Th style={{ width: rem(120) }}></Table.Th>
                        </Table.Tr>
                    </Table.Thead>
                    <Table.Tbody>
                        {filtered.length === 0 && (
                            <Table.Tr>
                                <Table.Td colSpan={5}><Text ta="center" c="dimmed">No posts found</Text></Table.Td>
                            </Table.Tr>
                        )}
                        {filtered.map((p) => {
                            const cover = p.images.find((i) => i.imageId === p.coverImageId);
                            return (
                                <Table.Tr key={p.postId}>
                                    <Table.Td>{cover ? (<Avatar src={cover.url} radius="sm" size={40} />) : (<Avatar radius="sm" size={40}><IconPhoto size={16} /></Avatar>)}</Table.Td>
                                    <Table.Td><Text fw={600}>{p.title}</Text></Table.Td>
                                    <Table.Td><Text lineClamp={2} className="ad-desc">{p.description}</Text></Table.Td>
                                    <Table.Td><Badge variant="light">{p.images.length} image{p.images.length === 1 ? '' : 's'}</Badge></Table.Td>
                                    <Table.Td>
                                        <Group gap="xs" justify="right">
                                            <Tooltip label="Edit"><ActionIcon variant="subtle" onClick={() => onEdit(p)} aria-label="edit-post"><IconEdit size={18} /></ActionIcon></Tooltip>
                                            <Tooltip label="Delete"><ActionIcon color="red" variant="subtle" onClick={() => onDelete(p.postId)} aria-label="delete-post"><IconTrash size={18} /></ActionIcon></Tooltip>
                                        </Group>
                                    </Table.Td>
                                </Table.Tr>
                            );
                        })}
                    </Table.Tbody>
                </Table>
            </ScrollArea>
        </Card>
    );
}

// ===== Main Admin Dashboard =====
export function AdminDashboard() {
    const [posts, setPosts] = useState<PostInterface[]>([]);
    const [loading, setLoading] = useState(true);
    const [search, setSearch] = useState('');
    const [modalOpened, modalCtrl] = useDisclosure(false);
    const [editing, setEditing] = useState<PostInterface | null>(null);
    const [notif, setNotif] = useState<{ type: 'success' | 'error'; msg: string; } | null>(null);
    const [saving, setSaving] = useState(false);
    const [formKey, setFormKey] = useState(0);
    const auth = useAuth();
    const { t } = useTranslation();
    const seoTitle = t('seo.adminTitle');
    const seoDescription = t('seo.adminDescription');

    useEffect(() => {
        (async () => {
            try {
                const data = await serverAccessor.getAllPosts();
                setPosts(data);
            } catch (e) {
                console.error('Failed to load posts', e);
            } finally {
                setLoading(false);
            }
        })();
    }, []);

    const openNew = () => {
        setEditing(null);
        setFormKey((k) => k + 1);
        modalCtrl.open();
    };
    const openEdit = (p: PostInterface) => {
        // deep copy so edits in the form don't mutate the list until you save
        const copy: PostInterface = {
            postId: p.postId,
            title: p.title,
            description: p.description,
            coverImageId: p.coverImageId,
            images: p.images.map(i => ({ ...i })),
        };
        setEditing(copy);
        setFormKey((k) => k + 1); // remount with the copied data
        modalCtrl.open();
    };

    const upsertPost = async (post: PostInterface) => {
        try {
            setSaving(true);

            // 1) Split images into new (data URLs) vs existing (already https)
            const newImgs = post.images.filter(i => i.url.startsWith('data:'));
            const keepImgs = post.images.filter(i => !i.url.startsWith('data:'));

            // 2) Upload new images to S3
            const uploadedImgs: PostImageInterface[] = [];
            for (const img of newImgs) {
                const { blob, contentType, ext } = dataUrlToBlob(img.url);
                const { uploadUrl, publicUrl } = await serverAccessor.presignImage(post.postId, img.imageId, ext, contentType, auth.token);
                await uploadBlobToS3(uploadUrl, blob, contentType);
                img.url = publicUrl; // <- final CDN/S3 url
                uploadedImgs.push(img);
            }

            const finalImages = [...keepImgs, ...uploadedImgs];

            // 3) Send post to your API (create or update)
            const exists = posts.some(p => p.postId === post.postId);
            const newPost: PostInterface = { ...post, images: finalImages };

            await exists ? serverAccessor.updateExistingPost(newPost, auth.token) : serverAccessor.uploadNewPost(newPost, auth.token);

            // 4) Update local state with server response
            setPosts(prev => {
                const idx = prev.findIndex(x => x.postId === newPost.postId);
                if (idx >= 0) {
                    const next = [...prev];
                    next[idx] = newPost;
                    return next;
                }
                return [newPost, ...prev];
            });

            setNotif({ type: 'success', msg: posts.some(p => p.postId === post.postId) ? 'Post updated' : 'Post created' });
        } catch (err: any) {
            console.error(err);
            setNotif({ type: 'error', msg: err?.message || 'Failed to save post' });
        } finally {
            setSaving(false);
        }
    };

    const deletePost = async (postId: string) => { 
        await serverAccessor.deletePost(postId, auth.token);
        setPosts((prev) => prev.filter((p) => p.postId !== postId)); 
        setNotif({ type: 'success', msg: 'Post deleted' }); 
    };

    return (
        <>
            <Seo title={seoTitle} description={seoDescription} canonicalPath="/admin" noindex />
            <Stack gap="md" className="ad-container" p="md">
            <Group justify="space-between" className="ad-toolbar">
                <Text fw={700} size="lg">Manage Posts</Text>
                <Group className="ad-actions">
                    <TextInput value={search} onChange={(e) => setSearch(e.currentTarget.value)} leftSection={<IconSearch size={16} />} placeholder="Search posts..." className="ad-search" />
                    <Button leftSection={<IconPlus size={18} />} onClick={openNew} className="ad-create-btn">New post</Button>
                </Group>
            </Group>

            {notif && (<Notification withCloseButton onClose={() => setNotif(null)} color={notif.type === 'success' ? 'green' : 'red'} icon={notif.type === 'success' ? <IconCheck /> : <IconAlertCircle />}>{notif.msg}</Notification>)}

            {loading ? (
                <Text c="dimmed">Cargando publicaciones...</Text>
            ) : (
                <PostsTable posts={posts} onEdit={openEdit} onDelete={deletePost} filter={search} />
            )}

            <PostFormModal key={formKey} saving={saving} opened={modalOpened} onClose={modalCtrl.close} initial={editing} onSubmit={upsertPost} />

            {/* User-facing preview grid using the carousel card */}
            {!loading && (
                <>
                    <Divider label="User-facing preview" labelPosition="center" my="lg" />
                    <SimpleGrid cols={{ base: 1, sm: 2, md: 3 }} spacing="lg">
                        {posts.map((p) => (
                            <PostCard key={p.postId} post={p} />
                        ))}
                    </SimpleGrid>
                </>
            )}
            </Stack>
        </>
    );
}

export default AdminDashboard;
